import { create } from 'zustand';

import { IVideoStream } from '@src/types';

import { isValidKey } from '@src/utils';
import { stopTracks } from '@src/helpers';

import { MAX_BOXES } from '@src/constants';

interface IVideoStreamStore {
  videoStreams: IVideoStream[];
  originalStream: MediaStream | null;
  modifiedStream: MediaStream | null;
  pip: { isActive: boolean; activeId: string };
  isReporter: boolean;
  setReporter: (isReporter: boolean) => void;
  setPip: (value: { isActive: boolean; activeId: string }) => void;
  appendVideo: (videoStream: IVideoStream) => void;
  updateVideo: (
    streamPeerId: string,
    videoStream: Partial<IVideoStream>,
    sortBy?: keyof IVideoStream,
    streamType?: 'videoType' | 'screenType'
  ) => void;
  removeVideo: (streamId: string) => void;
  removeVideoByPeerAndType: (peerId: string, type: string) => void;
  setOriginalStream: (stream: MediaStream | null) => void;
  setModifiedStream: (stream: MediaStream | null) => void;
  resetVideoStreams: () => void;
}

const defaultVideoStreamState: Pick<
  IVideoStreamStore,
  'videoStreams' | 'originalStream' | 'modifiedStream' | 'pip' | 'isReporter'
> = {
  videoStreams: [],
  originalStream: null,
  modifiedStream: null,
  pip: { isActive: false, activeId: '' },
  isReporter: false,
};

export const useVideoStreamStore = create<IVideoStreamStore>((set) => ({
  ...defaultVideoStreamState,

  setReporter: (isReporter) => set(() => ({ isReporter })),
  setPip: (value) =>
    set((state) => {
      return { ...state, pip: value };
    }),
  appendVideo: (videoStream) =>
    set((state) => {
      const updatedStreams = [...(state.videoStreams || [])];

      // For current user streams, match by peerId to prevent ID changes on camera reopen
      // For other users, match by id
      const streamIndex = videoStream.isCurrentUser
        ? updatedStreams.findIndex(
            ({ peerId = '', type }) => peerId === videoStream.peerId && type === videoStream.type
          )
        : updatedStreams.findIndex(({ id = '', type }) => id === videoStream.id && type === videoStream.type);

      if (streamIndex > -1) {
        // Stop tracks of the existing stream before replacing
        const existingStream = updatedStreams[streamIndex].stream;
        if (existingStream) {
          stopTracks(existingStream);
        }

        // Update existing entry - preserve the FIRST id to prevent React key changes
        const preservedId = updatedStreams[streamIndex].id;
        updatedStreams[streamIndex] = {
          ...updatedStreams[streamIndex],
          ...videoStream,
          // Keep original id for React key stability
          id: preservedId,
        };
        return { ...state, videoStreams: updatedStreams };
      }

      return { ...state, videoStreams: [...state.videoStreams, videoStream] };
    }),
  updateVideo: (streamPeerId, videoStream, sortBy, streamType) =>
    set((state) => {
      const updatedStreams = [...(state.videoStreams || [])];

      // If streamType is provided, match by both peerId and type to avoid updating wrong stream type
      const streamIndex = streamType
        ? updatedStreams.findIndex(({ peerId = '', type }) => peerId === streamPeerId && type === streamType)
        : updatedStreams.findIndex(({ peerId = '', id }) => [peerId, id].includes(streamPeerId));

      if (streamIndex > -1) {
        // If stream is being updated (replaced or set to null)
        if (videoStream.stream !== undefined) {
          const existingVideoMeta = updatedStreams[streamIndex];
          const oldStream = existingVideoMeta.stream;
          const newStream = videoStream.stream;

          // Only stop tracks if the stream instance has actually changed
          if (oldStream && oldStream !== newStream) {
            stopTracks(oldStream);
          }
        }

        updatedStreams[streamIndex] = { ...updatedStreams[streamIndex], ...videoStream };
        const numberOfVideos = updatedStreams.filter(({ type }) => type === 'videoType').length - 1;
        if (sortBy && isValidKey(updatedStreams[0], sortBy) && numberOfVideos > MAX_BOXES) {
          const sortedStreams = updatedStreams.sort((a, b) => +(b[sortBy] || 0) - +(a[sortBy] || 0));
          return { ...state, videoStreams: sortedStreams };
        } else {
          return { ...state, videoStreams: updatedStreams };
        }
      }
      return state;
    }),
  removeVideo: (streamId) =>
    set((state) => {
      state.videoStreams
        .find(({ id }) => id === streamId)
        ?.stream?.getTracks()
        ?.forEach((track) => track.stop());
      return { ...state, videoStreams: state.videoStreams.filter(({ id }) => id !== streamId) };
    }),
  removeVideoByPeerAndType: (peerId, type) =>
    set((state) => {
      const streamsToRemove = state.videoStreams.filter((stream) => stream.peerId === peerId && stream.type === type);

      // Stop tracks for streams being removed
      streamsToRemove.forEach(({ stream }) => {
        stream?.getTracks()?.forEach((track) => track.stop());
      });

      return {
        ...state,
        videoStreams: state.videoStreams.filter((stream) => !(stream.peerId === peerId && stream.type === type)),
      };
    }),
  setOriginalStream: (originalStream) =>
    set((state) => {
      // Only stop tracks if the stream instance has actually changed
      if (state.originalStream && state.originalStream !== originalStream) {
        stopTracks(state.originalStream);
      }
      return { ...state, originalStream };
    }),
  setModifiedStream: (modifiedStream) =>
    set((state) => {
      // Only stop tracks if the stream instance has actually changed
      if (state.modifiedStream && state.modifiedStream !== modifiedStream) {
        stopTracks(state.modifiedStream);
      }
      return { ...state, modifiedStream };
    }),
  resetVideoStreams: () =>
    set((state) => {
      state.originalStream && stopTracks(state.originalStream);
      state.modifiedStream && stopTracks(state.modifiedStream);
      state.videoStreams.forEach(({ stream }) => stream && stopTracks(stream));
      return { ...defaultVideoStreamState };
    }),
}));
